import { useRef, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { DateTimePicker } from '@mantine/dates'
import yup from '../../lib/yup'
import haxios from '../../lib/haxios'
import { ModalContainerAnimateDown } from '../gsys-ui/ModalContainer/ModalContainer'
import NiceModal from '@ebay/nice-modal-react'
import useApi from '../../hooks/useApi'

const validation = yup.object().shape({
  title: yup.string().required('Title is required').min(3, 'Title must be at least 3 characters'),
  expiryTime: yup
    .date()
    .required('Expiry time is required')
    .test('future', 'Expiry time must be in the future', (value) => {
      if (!value) return true
      return value > new Date()
    }),
  pdf: yup
    .mixed()
    .required('PDF is required')
    .test('fileType', 'File must be a PDF', (value) => value && value.type === 'application/pdf'),
  branches: yup
    .array()
    .of(yup.string())
    .min(1, 'Select at least one branch')
    .required('Select at least one branch')
})

const PromotionUploadModal = NiceModal.create((innerProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [garageSearch, setGarageSearch] = useState('')
  const regions = []
  const modalContainer = useRef(null)

  const garages = useApi({
    url: 'https://vision-web-api-test.azurewebsites.net/api/connectplus/garageusers',
    initial: []
  })

  const garageList = garages.data
    .map((garage) => ({
      GarageName: garage.GarageName,
      CustomerKeyCode: garage.CustomerKeyCode
    }))
    .filter(
      (garage, index, self) =>
        self.findIndex((g) => g.CustomerKeyCode === garage.CustomerKeyCode) === index
    )
    .sort((a, b) => a.GarageName.localeCompare(b.GarageName))

  // Filter garages based on search term
  const filteredGarageList = garageList.filter(garage =>
    garage.GarageName.toLowerCase().includes(garageSearch.toLowerCase()) ||
    garage.CustomerKeyCode.toLowerCase().includes(garageSearch.toLowerCase())
  )

  const handleClose = () => {
    modalContainer.current?.closeModal()
  }

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validation),
    defaultValues: innerProps?.defaultValues || {
      title: '',
      expiryTime: null,
      pdf: null,
      branches: []
    }
  })

  const handleFileSelect = (file) => {
    setValue('pdf', file, { shouldValidate: true })
    if (file) {
      const url = URL.createObjectURL(file)
      setPdfUrl(url)
    } else {
      setPdfUrl(null)
    }
  }

  console.log('Form values before submit:', control._formValues)

  const onSubmit = async (data) => {
    console.log('onSubmit called with data:', data)
    
    // Validate required fields before submission
    if (!data.expiryTime) {
      console.error('Expiry time is required')
      return
    }
    
    setIsLoading(true)
    try {
      const formData = new FormData()
      formData.append('pdf', data.pdf)
      formData.append('title', data.title)
      formData.append('expiryTime', data.expiryTime.toISOString())
      formData.append('branches', JSON.stringify(data.branches))

      const response = await (innerProps?.defaultValues?.id
        ? haxios.put(`/promotions/${innerProps?.defaultValues?.id}`, {
          title: data.title,
          expiryTime: data.expiryTime.toISOString(),
          branches: JSON.stringify(data.branches)
        })
        : haxios.post('/promotions/upload', formData))

      if (innerProps?.refetch) {
        await innerProps.refetch()
      }

      handleClose()
    } catch (error) {
      console.error('Error uploading promotion:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalContainerAnimateDown ref={modalContainer}>
      <Card sx={{
        width: '950px',
        background: 'linear-gradient(135deg, #34495E 0%, #2C3E50 100%)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        borderRadius: '12px'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form
            onSubmit={(e) => {
              console.log('Form submit event triggered')
              e.preventDefault()
              return handleSubmit(onSubmit)(e)
            }}
          >
            <Grid container spacing={3}>
              {/* Left Side - Form */}
              <Grid item xs={12} md={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Promotion title"
                          fullWidth
                          error={!!errors.title}
                          helperText={errors.title?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'white',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="expiryTime"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="datetime-local"
                          placeholder="Select expiry date and time"
                          fullWidth
                          value={field.value ? new Date(field.value.getTime() - field.value.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const date = new Date(e.target.value)
                            field.onChange(date)
                            setValue('expiryTime', date, { shouldValidate: true })
                          }}
                          error={!!errors.expiryTime}
                          helperText={errors.expiryTime?.message}
                          inputProps={{
                            min: new Date().toISOString().slice(0, 16)
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                              '&::placeholder': {
                                color: 'rgba(255, 255, 255, 0.5)',
                              },
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {!innerProps?.defaultValues?.pdf && (
                      <Controller
                        name="pdf"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="file"
                            inputProps={{ accept: "application/pdf" }}
                            onChange={(e) => {
                              const file = e.target.files[0]
                              field.onChange(file)
                              handleFileSelect(file)
                            }}
                            fullWidth
                            error={!!errors.pdf}
                            helperText={errors.pdf?.message}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#FF6B35',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#FF6B35',
                                },
                              },
                              '& .MuiInputBase-input': {
                                color: 'white',
                                padding: '12px 14px',
                                lineHeight: '1.5',
                                '&::file-selector-button': {
                                  backgroundColor: '#FF6B35',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  marginRight: '12px',
                                  cursor: 'pointer',
                                  verticalAlign: 'middle',
                                  '&:hover': {
                                    backgroundColor: '#E55A2B',
                                  },
                                },
                              },
                              '& .MuiFormHelperText-root': {
                                color: '#E74C3C',
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                      Select garages
                    </Typography>
                    {errors.branches && (
                      <Typography variant="body2" sx={{ color: '#E74C3C', mb: 2 }}>
                        {errors.branches.message}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      value={garageSearch}
                      onChange={(e) => setGarageSearch(e.target.value)}
                      placeholder="Search garages..."
                      size="small"
                      fullWidth
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#FF6B35',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#FF6B35',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                          '&::placeholder': {
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="branches"
                      control={control}
                      render={({ field }) => (
                        <Box sx={{
                          height: '365px',
                          overflowY: 'auto',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '12px',
                          p: 2,
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value.length === garageList.length && garageList.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    field.onChange(garageList.map((e) => e.CustomerKeyCode))
                                  } else {
                                    field.onChange([])
                                  }
                                }}
                                sx={{
                                  color: '#FF6B35',
                                  '&.Mui-checked': {
                                    color: '#FF6B35',
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                Select all ({garageList.length})
                              </Typography>
                            }
                            sx={{ mb: 2 }}
                          />
                          {filteredGarageList.length === 0 && garageSearch ? (
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', py: 2 }}>
                              No garages found matching "{garageSearch}"
                            </Typography>
                          ) : (
                            filteredGarageList.map((garage) => (
                              <Box key={garage.CustomerKeyCode} sx={{ py: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={field.value.includes(garage.CustomerKeyCode)}
                                      onChange={(e) => {
                                        const updatedBranches = e.target.checked
                                          ? [...field.value, garage.CustomerKeyCode]
                                          : field.value.filter((b) => b !== garage.CustomerKeyCode)
                                        field.onChange(updatedBranches)
                                      }}
                                      sx={{
                                        color: '#FF6B35',
                                        '&.Mui-checked': {
                                          color: '#FF6B35',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                      [{garage.CustomerKeyCode}] {garage.GarageName}
                                    </Typography>
                                  }
                                />
                              </Box>
                            ))
                          )}
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isLoading}
                        sx={{
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          px: 3,
                          '&:hover': {
                            borderColor: '#FF6B35',
                            backgroundColor: 'rgba(255, 107, 53, 0.1)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        sx={{
                          backgroundColor: '#FF6B35',
                          color: 'white',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          borderRadius: '12px',
                          px: 3,
                          boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                          '&:hover': {
                            backgroundColor: '#E55A2B',
                            boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
                          },
                          '&:disabled': {
                            backgroundColor: 'rgba(255, 107, 53, 0.5)',
                          },
                        }}
                      >
                        {isLoading ? 'Processing...' : (innerProps?.defaultValues?.id ? 'Save' : 'Upload')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right Side - Preview */}
              <Grid item xs={12} md={5}>
                <Box sx={{
                  overflow: 'hidden',
                  width: '100%',
                  height: '500px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }}>
                  {pdfUrl || innerProps?.defaultValues?.pdf ? (
                    <Box
                      component="iframe"
                      src={pdfUrl || innerProps?.defaultValues?.pdf}
                      sx={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      title="PDF Preview"
                    />
                  ) : (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }}>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        No PDF selected
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </ModalContainerAnimateDown>
  )
})

export default PromotionUploadModal
